import { Link } from '@tanstack/react-router'
import type { Product, SiteConfig } from '@/types'
import { ProductCard } from './ProductCard'

interface EcommerceHomepageProps {
  products: Product[]
  siteConfig: SiteConfig | null
}

export function EcommerceHomepage({ products, siteConfig }: EcommerceHomepageProps) {
  const featuredProducts = products.filter((p: Product & { is_featured?: boolean }) => p.is_featured).slice(0, 4)
  const displayProducts = featuredProducts.length > 0 ? featuredProducts : products.slice(0, 8)
  const saleProducts = products.filter(p => p.compare_at_price && p.compare_at_price > p.price).slice(0, 4)

  const brandName = siteConfig?.name || 'Our Store'
  const tagline = getTagline(siteConfig?.slug)
  const currencySymbol = siteConfig?.settings?.currency_symbol || '₹'

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="absolute inset-0 bg-black/20" />
        <div className="relative container mx-auto px-4 py-24 md:py-32">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              {tagline.headline}
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
              {tagline.subheadline}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/products"
                search={{}}
                className="inline-flex items-center px-8 py-4 bg-white text-gray-900 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
              >
                Shop Collection
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-colors"
              >
                Our Story
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      {displayProducts.length > 0 && (
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Featured Collection</h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                Discover our curated selection of exceptional pieces
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {displayProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
            <div className="text-center mt-12">
              <Link
                to="/products"
                search={{}}
                className="inline-flex items-center px-8 py-3 border-2 border-gray-900 text-gray-900 font-semibold rounded-lg hover:bg-gray-900 hover:text-white transition-colors"
              >
                View All Products
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Brand Story Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Crafted with Purpose</h2>
              <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                Every piece in our collection tells a story of craftsmanship, quality materials,
                and timeless design. We work with skilled artisans to bring you furniture and
                decor that transforms spaces into sanctuaries.
              </p>
              <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                Our commitment to sustainability means we source responsibly and create pieces
                that are built to last, becoming cherished parts of your home for years to come.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center text-gray-900 font-semibold hover:underline"
              >
                Learn more about us
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-200 rounded-lg aspect-square" />
              <div className="bg-gray-300 rounded-lg aspect-square mt-8" />
            </div>
          </div>
        </div>
      </section>

      {/* Sale Section */}
      {saleProducts.length > 0 && (
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1 bg-red-100 text-red-700 text-sm font-medium rounded-full mb-4">
                Limited Time
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Special Offers</h2>
              <p className="text-gray-600 text-lg">
                Don't miss out on these exceptional deals
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {saleProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
              </div>
              <h3 className="font-semibold mb-2">Free Shipping</h3>
              <p className="text-gray-400 text-sm">On orders over {currencySymbol}10,000</p>
            </div>
            <div>
              <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="font-semibold mb-2">Quality Assured</h3>
              <p className="text-gray-400 text-sm">Handcrafted excellence</p>
            </div>
            <div>
              <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <h3 className="font-semibold mb-2">Easy Returns</h3>
              <p className="text-gray-400 text-sm">30-day return policy</p>
            </div>
            <div>
              <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <h3 className="font-semibold mb-2">Expert Support</h3>
              <p className="text-gray-400 text-sm">Design consultation available</p>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Stay Inspired</h2>
          <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
            Subscribe to our newsletter for exclusive offers, design tips, and new arrivals
          </p>
          <form className="max-w-md mx-auto flex gap-3">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-gray-900 focus:outline-none"
            />
            <button
              type="submit"
              className="px-6 py-3 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors"
            >
              Subscribe
            </button>
          </form>
        </div>
      </section>
    </div>
  )
}

function getTagline(slug: string | undefined): { headline: string; subheadline: string } {
  switch (slug) {
    case 'astaka':
      return {
        headline: 'Luxury Furniture & Decor',
        subheadline: 'Transform your space with our curated collection of premium furniture and exquisite home accessories.'
      }
    case 'nadi':
      return {
        headline: 'Furniture Makers & Curators',
        subheadline: 'Discover handcrafted furniture pieces that blend traditional craftsmanship with contemporary design.'
      }
    default:
      return {
        headline: 'Exceptional Home Furnishings',
        subheadline: 'Discover our collection of thoughtfully designed furniture and home decor pieces.'
      }
  }
}
